import { apiCall } from '../../services/apiUtil';

/**
 * Tournament API Service
 *
 * Centralized API calls for all tournament-related operations
 */

// ============================================================================
// Tournament CRUD Operations
// ============================================================================

export const tournamentAPI = {
  create: async (tournamentData) => {
    const payload = {
      tournamentName: tournamentData.tournamentName,
      description: tournamentData.description,
      startDate: tournamentData.startDate,
      endDate: tournamentData.endDate,
      registrationDeadline: tournamentData.registrationDeadline || null,
      maxParticipants: tournamentData.maxParticipants,
      registrationFee: tournamentData.registrationFee,
      participantsType: tournamentData.participantsType,
      playersPerTeam: tournamentData.playersPerTeam,
      sportTypeId: tournamentData.sportTypeId ?? null,
      facilityId: tournamentData.facilityId ?? null,
      venueName: tournamentData.venueName ?? null,
      venueAddress: tournamentData.venueAddress ?? null,
      venuePlaceId: tournamentData.venuePlaceId ?? null,
      venueLat: tournamentData.venueLat ?? null,
      venueLng: tournamentData.venueLng ?? null,
      organizerUserId: tournamentData.organizerUserId ?? null,
      difficultyLevel: tournamentData.difficultyLevel,
      tournamentImage: tournamentData.tournamentImage,
      ownerType: tournamentData.ownerType,
      ownerId: tournamentData.ownerId,
      prizeDetail: tournamentData.prizeDetail,
      status: tournamentData.status,
      matchConfiguration: tournamentData.matchConfiguration,
      playNow: tournamentData.playNow,
    };

    return apiCall('/tournaments', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  getById: async (id) => {
    return apiCall(`/tournaments/${id}`);
  },

  getAll: async (filters = {}) => {
    const queryParams = new URLSearchParams();

    if (filters.sportTypeId) queryParams.append('sportTypeId', filters.sportTypeId);
    if (filters.facilityId) queryParams.append('facilityId', filters.facilityId);
    if (filters.organizerUserId) queryParams.append('organizerUserId', filters.organizerUserId);
    if (filters.involvedUserId) queryParams.append('involvedUserId', filters.involvedUserId);
    if (filters.status) queryParams.append('status', filters.status);
    if (filters.excludeCompleted) queryParams.append('excludeCompleted', 'true');
    if (filters.difficultyLevel) queryParams.append('difficultyLevel', filters.difficultyLevel);
    if (filters.ownerType) queryParams.append('ownerType', filters.ownerType);
    if (filters.ownerId) queryParams.append('ownerId', filters.ownerId);
    queryParams.append('limit', '100');

    return apiCall(`/tournaments?${queryParams.toString()}`);
  },

  getMyTournaments: async (organizerUserId, filters = {}) => {
    const queryParams = new URLSearchParams({ organizerUserId, limit: '100' });
    if (filters.sportTypeId) queryParams.append('sportTypeId', filters.sportTypeId);
    if (filters.excludeCompleted) queryParams.append('excludeCompleted', 'true');
    return apiCall(`/tournaments?${queryParams.toString()}`);
  },

  update: async (id, tournamentData) => {
    return apiCall(`/tournaments/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(tournamentData),
    });
  },

  delete: async (id) => {
    return apiCall(`/tournaments/${id}`, {
      method: 'DELETE',
    });
  },

  generateSchedule: async (id, options = {}) => {
    return apiCall(`/tournaments/${id}/generate-schedule`, {
      method: 'POST',
      body: JSON.stringify(options),
    });
  },

  deleteSchedule: async (id) => {
    return apiCall(`/tournaments/${id}/schedule`, {
      method: 'DELETE',
    });
  },

  // Manual trigger for the backend's generic auto-seeding pass (see
  // resolvePendingSeeds in scheduleGenerator.js) — recordResult already
  // calls this automatically after every match result, so this is only
  // useful for testing/on-demand re-checks.
  resolveSeeds: async (id) => {
    return apiCall(`/tournaments/${id}/resolve-seeds`, {
      method: 'POST',
    });
  },

  deleteAllParticipants: async (id) => {
    return apiCall(`/tournaments/${id}/participants`, {
      method: 'DELETE',
    });
  },

  quickCreate: async (data) => {
    return apiCall('/tournaments/quick', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Group flat array of games by round_number.
 *
 * For group_knockout tournaments, round_number restarts at 1 within each
 * pool (see generateGroupKnockout on the backend), so round_number alone
 * isn't a unique round identity — pool A's "Round 1" and pool B's "Round 1"
 * share a round_number but are different rounds. Grouping also by group_name
 * keeps them as separate round entries instead of merging their games under
 * one mislabeled round. roundKey is the resulting unique identity — use it
 * (not round_number) for React keys / collapse state, since round_number by
 * itself can still collide across groups.
 * @param {Array} games - Flat array of games
 * @returns {Array} Array of round objects with nested games
 */
const groupGamesByRound = (games) => {
  const roundsMap = {};

  for (const game of games) {
    const rNum = game.round_number;
    const roundKey = game.group_name ? `${rNum}::${game.group_name}` : String(rNum);
    if (!roundsMap[roundKey]) {
      roundsMap[roundKey] = {
        roundKey,
        round_number: rNum,
        round_name: game.round_name,
        round_type: game.round_type,
        group_name: game.group_name,
        games: []
      };
    }
    roundsMap[roundKey].games.push(game);
  }

  return Object.values(roundsMap);
};

// ============================================================================
// Tournament Schedule Operations
// ============================================================================

export const tournamentScheduleAPI = {
  // Get all games grouped by round (backend returns flat, we group on frontend)
  getRounds: async (tournamentId) => {
    const flatGames = await apiCall(`/tournaments/${tournamentId}/rounds`);
    return groupGamesByRound(flatGames);
  },

  // Get all games as flat list with optional filters
  // Note: Use this for flat data, use getRounds() for grouped data
  getFilteredRounds: async (tournamentId, filters = {}) => {
    const allGames = await apiCall(`/tournaments/${tournamentId}/rounds`);

    // Apply filters on frontend
    let filtered = allGames;
    if (filters.roundNumber) {
      filtered = filtered.filter(g => g.round_number === filters.roundNumber);
    }
    if (filters.status) {
      filtered = filtered.filter(g => g.status === filters.status);
    }
    if (filters.groupName) {
      filtered = filtered.filter(g => g.group_name === filters.groupName);
    }

    return filtered;
  },

  // Get a single game by ID
  getGame: async (gameId) => {
    return apiCall(`/tournament-rounds/${gameId}`);
  },

  // Update game (date, venue, status)
  updateGame: async (gameId, data) => {
    return apiCall(`/tournament-rounds/${gameId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  // Record game result
  recordResult: async (gameId, resultData) => {
    return apiCall(`/tournament-rounds/${gameId}/record-result`, {
      method: 'POST',
      body: JSON.stringify(resultData),
    });
  },

  // Check-in a participant for a game
  checkIn: async (gameId, data) => {
    return apiCall(`/tournament-rounds/${gameId}/checkin`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Remove check-in (undo)
  removeCheckIn: async (gameId, data) => {
    return apiCall(`/tournament-rounds/${gameId}/checkin`, {
      method: 'DELETE',
      body: JSON.stringify(data),
    });
  },

  // Get check-ins for a game
  getCheckIns: async (gameId) => {
    return apiCall(`/tournament-rounds/${gameId}/checkins`);
  },

  // Start a game (requires both participants checked in)
  startGame: async (gameId) => {
    return apiCall(`/tournament-rounds/${gameId}/start`, {
      method: 'POST',
    });
  },

  // Create a new round
  createRound: async (tournamentId, roundData) => {
    return apiCall(`/tournaments/${tournamentId}/rounds`, {
      method: 'POST',
      body: JSON.stringify(roundData),
    });
  },

  // Delete a round
  deleteRound: async (tournamentId, roundNumber) => {
    return apiCall(`/tournaments/${tournamentId}/rounds/${roundNumber}`, {
      method: 'DELETE',
    });
  },

  // Create a new match in a round
  createMatch: async (tournamentId, matchData) => {
    return apiCall(`/tournaments/${tournamentId}/matches`, {
      method: 'POST',
      body: JSON.stringify(matchData),
    });
  },

  // Delete a match
  deleteMatch: async (matchId) => {
    return apiCall(`/tournament-rounds/${matchId}`, {
      method: 'DELETE',
    });
  },

  // Atomically swap the order (match_number) of two matches in the same round.
  // Must go through a single backend call, not two separate PATCHes — see
  // swapMatchOrder in tournamentScheduleController.js for why.
  swapMatchOrder: async (matchAId, matchBId) => {
    return apiCall('/tournament-rounds/swap-order', {
      method: 'POST',
      body: JSON.stringify({ matchAId, matchBId }),
    });
  },

  // Seed playoffs with top teams from standings (for IPL format)
  seedPlayoffs: async (tournamentId) => {
    return apiCall(`/tournaments/${tournamentId}/seed-playoffs`, {
      method: 'POST',
    });
  },
};

// ============================================================================
// Tournament Standings Operations
// ============================================================================

export const tournamentStandingsAPI = {
  getByTournament: async (tournamentId, filters = {}) => {
    const queryParams = new URLSearchParams();
    if (filters.groupName) queryParams.append('groupName', filters.groupName);
    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `/tournaments/${tournamentId}/standings?${queryString}`
      : `/tournaments/${tournamentId}/standings`;
    return apiCall(endpoint);
  },

  recalculate: async (tournamentId) => {
    return apiCall(`/tournament-standings/${tournamentId}/recalculate`, {
      method: 'POST',
    });
  },
};

// ============================================================================
// Tournament Team Groups (e.g. MLP cross-group team clusters)
// ============================================================================

export const tournamentTeamGroupsAPI = {
  getByTournament: async (tournamentId) => {
    return apiCall(`/tournaments/${tournamentId}/team-groups`);
  },

  // Replaces all group membership for the tournament with the given groups:
  // [{ groupName: 'Group 1', teamIds: [20, 21] }, ...]
  setTeamGroups: async (tournamentId, groups) => {
    return apiCall(`/tournaments/${tournamentId}/team-groups`, {
      method: 'PUT',
      body: JSON.stringify({ groups }),
    });
  },
};

// ============================================================================
// Tournament Participant Operations
// ============================================================================

export const tournamentParticipantAPI = {
  register: async (participantData) => {
    // Validate: must have either teamId or userId
    if (!participantData.teamId && !participantData.userId) {
      throw new Error('Registration requires either a team or user ID');
    }

    // The API expects either participant_id OR team_id, not both
    const payload = {
      tournamentId: participantData.tournamentId,
      ...(participantData.teamId
        ? { teamId: participantData.teamId }
        : { participantId: participantData.userId }
      ),
      ...(participantData.roles && {
        coowner: participantData.roles.coowner || false,
        player: participantData.roles.player || false,
        coach: participantData.roles.coach || false,
        parents: participantData.roles.parents || false,
        teamOwner: participantData.roles.teamOwner || false,
        referee: participantData.roles.referee || false,
        scorer: participantData.roles.scorer || false
      }),
      // Callers have always passed these two; the payload used to drop them on
      // the floor, so tournament terms acceptance was never actually recorded.
      agreeToTerms: participantData.agreeToTerms === true,
      agreeToWaiver: participantData.agreeToWaiver === true,
      // The amount is derived from the tournament server-side — we only say how.
      ...(participantData.paymentMethod && { paymentMethod: participantData.paymentMethod }),
      isActive: true
    };

    return apiCall('/tournament-participants', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  getByTournamentId: async (tournamentId, page = 1, limit = 10) => {
    const queryParams = new URLSearchParams({
      tournamentId: tournamentId.toString(),
      page: page.toString(),
      limit: limit.toString()
    });
    return apiCall(`/tournament-participants?${queryParams.toString()}`);
  },

  getByUserId: async (userId) => {
    return apiCall(`/tournament-participants?participantId=${userId}`);
  },

  getById: async (id) => {
    return apiCall(`/tournament-participants/${id}`);
  },

  update: async (id, participantData) => {
    return apiCall(`/tournament-participants/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(participantData),
    });
  },

  delete: async (id) => {
    return apiCall(`/tournament-participants/${id}`, {
      method: 'DELETE',
    });
  },

  checkRegistration: async (tournamentId, userId, teamIds = []) => {
    // Check individual registration
    const individualCheck = apiCall(`/tournament-participants?tournamentId=${tournamentId}&participantId=${userId}`);

    // Also check team registrations for all of the user's teams in one batched call
    const teamCheck = teamIds.length > 0
      ? apiCall(`/tournament-participants?tournamentId=${tournamentId}&teamIds=${teamIds.join(',')}&limit=100`)
      : Promise.resolve(null);

    const [individualResult, teamResult] = await Promise.all([individualCheck, teamCheck]);

    const individualData = individualResult?.data || individualResult || [];
    const teamData = teamResult?.data || teamResult || [];

    // Combine and deduplicate by id
    const allData = [...individualData];
    teamData.forEach(teamReg => {
      if (!allData.some(existing => existing.id === teamReg.id)) {
        allData.push(teamReg);
      }
    });

    return { data: allData };
  },

  updateRegistration: async (registrationId, updateData) => {
    return apiCall(`/tournament-participants/${registrationId}`, {
      method: 'PATCH',
      body: JSON.stringify(updateData),
    });
  },

  withdrawRegistration: async (registrationId) => {
    return apiCall(`/tournament-participants/${registrationId}`, {
      method: 'DELETE',
    });
  }
};
